import type {
    ApiSpinTogetherDisplayConfigAny,
    ApiSpinTogetherDisplayConfigV2,
    ApiSpinTogetherFriendStateAny,
} from "@bridge-applications/pb-types";
import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import timerIcon from "../../../../../../../assets/static/icons/spin-together/timer-icon.webp";
import { spinTogetherActiveBoostSummary } from "../../../../../../../logic/helpers/spinTogetherBenefits";
import {
    ButtonVar1,
    type ButtonVar1Colors,
} from "../../../../../../common/ButtonVar1/ButtonVar1";
import { SpinTogetherRulesDialog } from "./components/SpinTogetherRulesDialog/SpinTogetherRulesDialog";
import "./SpinTogetherBoostIndicator.scss";

const BOOST_BUTTON_COLORS: ButtonVar1Colors = {
    faceFill: `linear-gradient(
        to bottom,
        #f8d869 0%,
        #fbd96d 5%,
        #fbda6f 11%,
        #fbd66e 19%,
        #fed468 28%,
        #fbcf64 37%,
        #fbcf63 47%,
        #fcc95c 54%,
        #f6c056 60%,
        #f3b24a 72%,
        #edaa41 80%,
        #e5a03b 89%,
        #db9536 100%
    )`,
    faceStroke: `linear-gradient(
        to bottom,
        #fde38f 0%,
        #f7de8e 6%,
        #fbdf8c 13%,
        #fcdf81 21%,
        #fbdd85 27%,
        #f8d67c 36%,
        #fbd46f 44%,
        #facf69 52%,
        #f7c761 58%,
        #f6c55e 66%,
        #f4c364 72%,
        #efc16c 79%,
        #f2c067 86%,
        #f5b553 92%,
        #eda943 96%,
        #fbca7c 100%
    )`,
    containerFill: `linear-gradient(
        to bottom,
        #bd7626 0%,
        #bd7626 82%,
        #ba7f33 86%,
        #b46d1d 92%,
        #ac6a1e 95%,
        #b0712e 97%,
        #ab7038 100%
    )`,
    containerStroke: "#000",
};

interface SpinTogetherBoostIndicatorProps {
    config: ApiSpinTogetherDisplayConfigAny | null;
    friends: readonly ApiSpinTogetherFriendStateAny[];
    locale: string;
    className?: string;
}

interface OverflowingCountProps {
    text: string;
}

interface OverflowingCountStyle extends CSSProperties {
    "--spin-together-count-offset": string;
}

function OverflowingCount({ text }: OverflowingCountProps) {
    const viewportRef = useRef<HTMLSpanElement>(null);
    const textRef = useRef<HTMLSpanElement>(null);
    const [overflowDistance, setOverflowDistance] = useState(0);

    useEffect(() => {
        const viewport = viewportRef.current;
        const textElement = textRef.current;
        if (!viewport || !textElement) return;

        const updateOverflow = () => {
            const viewportStyle = window.getComputedStyle(viewport);
            const horizontalPadding =
                Number.parseFloat(viewportStyle.paddingLeft) +
                Number.parseFloat(viewportStyle.paddingRight);
            const availableWidth = viewport.clientWidth - horizontalPadding;
            const nextOverflowDistance = Math.max(
                0,
                Math.ceil(textElement.scrollWidth - availableWidth),
            );
            setOverflowDistance((currentDistance) =>
                currentDistance === nextOverflowDistance
                    ? currentDistance
                    : nextOverflowDistance,
            );
        };

        updateOverflow();
        const resizeObserver = new ResizeObserver(updateOverflow);
        resizeObserver.observe(viewport);
        resizeObserver.observe(textElement);
        return () => resizeObserver.disconnect();
    }, [text]);

    const textStyle: OverflowingCountStyle = {
        "--spin-together-count-offset": `${-overflowDistance}px`,
    };

    return (
        <span
            className="spin-together-boost-indicator__count"
            ref={viewportRef}
        >
            <span
                className={[
                    "spin-together-boost-indicator__count-text",
                    overflowDistance > 0 && "is-overflowing",
                ]
                    .filter(Boolean)
                    .join(" ")}
                ref={textRef}
                style={textStyle}
            >
                {text}
            </span>
        </span>
    );
}

function displayConfigV2(
    config: ApiSpinTogetherDisplayConfigAny | null,
): ApiSpinTogetherDisplayConfigV2 | null {
    return config !== null && "policySchemaVersion" in config ? config : null;
}

function formatBasisPoints(basisPoints: number, locale: string): string {
    return new Intl.NumberFormat(locale, {
        style: "percent",
        maximumFractionDigits: 2,
    }).format(basisPoints / 10_000);
}

function formatCountdown(endsAt: string, nowMs: number): string {
    const remainingSeconds = Math.max(
        0,
        Math.ceil((Date.parse(endsAt) - nowMs) / 1_000),
    );
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
        2,
        "0",
    )}`;
}

export function SpinTogetherBoostIndicator({
    config,
    friends,
    locale,
    className,
}: SpinTogetherBoostIndicatorProps) {
    const { t } = useTranslation();
    const [nowMs, setNowMs] = useState(() => Date.now());
    const [isRulesOpen, setIsRulesOpen] = useState(false);
    const v2Config = displayConfigV2(config);
    const activeBoost = spinTogetherActiveBoostSummary(
        config,
        friends,
        nowMs,
    );
    const hasActiveBoost = activeBoost !== null;

    useEffect(() => {
        if (!hasActiveBoost) return;
        const intervalId = window.setInterval(() => {
            setNowMs(Date.now());
        }, 1_000);
        return () => window.clearInterval(intervalId);
    }, [hasActiveBoost]);

    const percentage = activeBoost
        ? formatBasisPoints(activeBoost.basisPoints, locale)
        : null;
    const countdown = activeBoost
        ? formatCountdown(activeBoost.endsAt, nowMs)
        : null;
    const bonusLabel = percentage
        ? t("spinTogether.indicator.bonus", { percentage })
        : null;

    return (
        <>
            {activeBoost && percentage && countdown && bonusLabel && (
                <ButtonVar1
                    type="button"
                    className={["spin-together-boost-indicator", className]
                        .filter(Boolean)
                        .join(" ")}
                    colors={BOOST_BUTTON_COLORS}
                    padding={0}
                    aria-label={t("spinTogether.indicator.openRules", {
                        percentage,
                        count: activeBoost.activeFriendCount,
                        time: countdown,
                    })}
                    onClick={() => setIsRulesOpen(true)}
                    content={
                        <span className="spin-together-boost-indicator__content">
                            <span className="spin-together-boost-indicator__timer">
                                <img
                                    className="spin-together-boost-indicator__timer-icon"
                                    src={timerIcon}
                                    alt=""
                                    aria-hidden="true"
                                    draggable={false}
                                />
                                <span
                                    className="spin-together-boost-indicator__time"
                                    dir="ltr"
                                >
                                    {countdown}
                                </span>
                            </span>
                            <span className="spin-together-boost-indicator__copy">
                                <span
                                    className="spin-together-boost-indicator__bonus"
                                    data-text={bonusLabel}
                                    dir="ltr"
                                >
                                    <span className="spin-together-boost-indicator__bonus-fill">
                                        {bonusLabel}
                                    </span>
                                </span>
                                <OverflowingCount
                                    text={t(
                                        "spinTogether.indicator.activeFriends",
                                        {
                                            count: activeBoost.activeFriendCount,
                                        },
                                    )}
                                />
                            </span>
                        </span>
                    }
                />
            )}

            {v2Config && percentage && countdown && (
                <SpinTogetherRulesDialog
                    config={v2Config}
                    isOpen={isRulesOpen}
                    locale={locale}
                    onClose={() => setIsRulesOpen(false)}
                    percentage={percentage}
                    timeLeft={countdown}
                />
            )}
        </>
    );
}
